export type StudyMaterialPlacement = "materialsPage" | "lessonPage";

export type StudyMaterialLink = {
    /**
     * A normal destination, or a placement-specific tracking URL. Leave null
     * until a verified destination has been chosen.
     */
    url: string | null;
    /**
     * Set only when this exact destination is an affiliate URL. Keeping this
     * separate prevents ordinary links from being described as affiliate links.
     */
    affiliateUrl: string | null;
    /** Optional ID for a link provider that supports placement-level tracking. */
    trackingId: string | null;
};

export type StudyMaterial = {
    id: string;
    title: string;
    type: string;
    description: string;
    suitableFor: string;
    recommendation: string;
    details?: Array<{ label: string; value: string }>;
    coverImage?: { src: string; alt: string };
    links: Record<StudyMaterialPlacement, StudyMaterialLink>;
};

export const studyMaterials: StudyMaterial[] = [
    {
        id: "genki-1-textbook",
        title: "Genki I: An Integrated Course in Elementary Japanese",
        type: "Textbook",
        description:
            "A structured beginner textbook that introduces practical grammar, vocabulary, reading, listening, and everyday Japanese through connected lessons.",
        suitableFor: "Beginners studying approximately JLPT N5 / CEFR A1.",
        recommendation:
            "This is the book I personally use in my online Japanese classes, and it was recommended by my teacher. Its lesson sequence makes it especially useful when you want clear explanations alongside regular practice.",
        details: [
            { label: "Edition", value: "Third Edition" },
            { label: "Volume", value: "Volume 1" },
            { label: "Language", value: "English" },
            { label: "Publisher", value: "The Japan Times Publishing" },
            { label: "Format", value: "Paperback" },
            { label: "ISBN-10 / ASIN", value: "4789017303" },
            { label: "ISBN-13", value: "9784789017305" },
            { label: "Coverage", value: "Lessons 1–12" },
        ],
        coverImage: {
            src: "/images/genki-i-third-edition.jpg",
            alt: "Cover of GENKI I: An Integrated Course in Elementary Japanese, Third Edition, Volume 1",
        },
        links: {
            // This is a clean, non-affiliate product URL. Replace it with an
            // affiliate URL only when an approved tracking destination exists.
            materialsPage: { url: "https://www.amazon.es/dp/4789017303", affiliateUrl: null, trackingId: null },
            lessonPage: { url: null, affiliateUrl: null, trackingId: null },
        },
    },
    {
        id: "genki-1-workbook",
        title: "Genki I Workbook",
        type: "Workbook",
        description:
            "Companion exercises for the Genki I lessons, with additional written, listening, and vocabulary practice to help turn new material into recallable skills.",
        suitableFor:
            "Genki I learners who benefit from guided homework, review between classes, or a more consistent independent study routine.",
        recommendation:
            "I recommend using it alongside the textbook because the extra practice reinforces the same lesson sequence used in my online Japanese classes. It was part of the Genki approach my teacher recommended to me.",
        coverImage: {
            src: "/images/genki-i-workbook.avif",
            alt: "Cover of the Genki I Workbook",
        },
        links: {
            materialsPage: { url: null, affiliateUrl: null, trackingId: null },
            lessonPage: { url: null, affiliateUrl: null, trackingId: null },
        },
    },
];

export function getStudyMaterialLink(material: StudyMaterial, placement: StudyMaterialPlacement) {
    const link = material.links[placement];
    const href = link.affiliateUrl || link.url;

    return {
        href,
        isAffiliate: Boolean(link.affiliateUrl),
        trackingId: link.trackingId,
    };
}
